import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Navigate, useParams } from 'react-router-dom'
import { z } from 'zod'
import {
  OwnerCreateBranchRequestSchema,
  OwnerPasswordSchema,
  OwnerSetupProfileRequestSchema,
  type OwnerCreateBranchRequest,
  type OwnerSetupProfileRequest,
} from '@naya/shared'

import { OwnerApiError } from '../ownerApi.js'
import { useOwnerSession } from '../hooks/useOwnerSession.js'
import {
  useSetupCheckEmail,
  useSetupConsume,
  useSetupProfile,
  useSetupValidate,
} from '../hooks/useSetup.js'
import { useCreateBranch } from '../hooks/useBranches.js'
import { BusinessNameForm } from '../components/BusinessNameForm.js'
import { LogoUploader } from '../components/LogoUploader.js'
import { OwnerThemeToggle } from '../components/OwnerThemeToggle.js'
import { PasswordInput } from '../../shared/PasswordInput.js'

/**
 * Owner setup wizard — a single component holding step state locally (no
 * DB-stored completion flag; setup completion is derived elsewhere from
 * branches.count).
 *
 * Two entry points share this component:
 *  - `/setup/:token`   — full wizard. Validates the token on mount; step 1
 *                        (password) consumes it and opens the owner session.
 *  - `/setup/continue` — resume for an already-authenticated owner (the
 *                        dashboard's "complete setup" CTA). Skips the password
 *                        step and starts at business-name.
 *
 * If the owner closes the browser mid-wizard after step 1, they now have a
 * session — logging in lands them on /dashboard, whose CTA routes back here
 * via /setup/continue.
 */

// Onboarding steps. Admin no longer types the owner's email/name or business
// name at tenant create — the owner fills those here. Password stays first
// (it consumes the setup token and opens the session). The slug is NOT a
// step: it's derived from the business name server-side (see Part 1 of the
// onboarding rework / services/tenants/slug.ts).
type WizardStep =
  | 'profile'
  | 'password'
  | 'business_name'
  | 'logo'
  | 'branch'
  | 'complete'

// The owner enters profile (email + name) FIRST, then password. Profile
// values are held in memo and submitted to /consume with the password as a
// single transaction (the token can't authenticate a PATCH /profile call
// before consume).
const STEP_ORDER: WizardStep[] = [
  'profile',
  'password',
  'business_name',
  'logo',
  'branch',
  'complete',
]

const PROGRESS_STEPS: WizardStep[] = [
  'profile',
  'password',
  'business_name',
  'logo',
  'branch',
]
const TOTAL_STEPS = PROGRESS_STEPS.length

// Back-navigation wall: the password step consumes the one-time setup token,
// so once it's done there's no going back into profile/password (token spent,
// session open). Only these steps render a Back button:
//   - 'password' → 'profile'      (both pre-consume, in-memory only)
//   - 'logo'     → 'business_name' (post-consume, idempotent PATCH)
//   - 'branch'   → 'logo'          (post-consume, idempotent)
// 'business_name' is never back-navigable — token mode arrives there past the
// consume wall, resume mode starts there.
function prevStep(step: WizardStep): WizardStep {
  const idx = STEP_ORDER.indexOf(step)
  return STEP_ORDER[Math.max(0, idx - 1)]!
}

function WizardProgress({ current }: { current: WizardStep }) {
  const currentIdx = STEP_ORDER.indexOf(current)
  return (
    <div className="owner-wizard-steps" aria-hidden="true">
      {PROGRESS_STEPS.map((step) => {
        const idx = STEP_ORDER.indexOf(step)
        const cls =
          idx < currentIdx
            ? 'is-done'
            : idx === currentIdx
              ? 'is-current'
              : ''
        return <div key={step} className={`owner-wizard-step-dot ${cls}`} />
      })}
    </div>
  )
}

// ----- step 1: password -----

const PasswordFormSchema = z
  .object({
    password: OwnerPasswordSchema,
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  })
type PasswordForm = z.infer<typeof PasswordFormSchema>

function PasswordStep({
  token,
  profile,
  onDone,
  onBack,
}: {
  token: string
  // Slice 6 — wizard's step 1 collected the owner's real email + name;
  // we send them along with the consume so the very first authenticated
  // request sees the real identity.
  profile: { email: string; display_name: string } | null
  onDone: () => void
  /** Return to the profile step (pre-consume only — both are in-memory). */
  onBack: () => void
}) {
  const consume = useSetupConsume()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordForm>({ resolver: zodResolver(PasswordFormSchema) })

  function onSubmit(values: PasswordForm) {
    consume.mutate(
      {
        token,
        password: values.password,
        ...(profile
          ? { email: profile.email, display_name: profile.display_name }
          : {}),
      },
      {
        onSuccess: () => onDone(),
        onError: (err) => {
          setError('root', {
            message:
              err instanceof OwnerApiError
                ? err.userMessage
                : 'Could not set your password. Please try again.',
          })
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="owner-wizard-step-label">Step 2 of {TOTAL_STEPS}</div>
      <h2>Set your password</h2>
      <p className="owner-card-sub">
        Choose a password to secure your account. At least 10 characters.
      </p>

      {errors.root && (
        <div className="owner-auth-error" role="alert">
          {errors.root.message}
        </div>
      )}

      <div className="owner-field">
        <label className="owner-label" htmlFor="password">
          Password
        </label>
        <PasswordInput
          id="password"
          classPrefix="owner"
          autoComplete="new-password"
          aria-invalid={errors.password ? 'true' : undefined}
          {...register('password')}
        />
        {errors.password && (
          <p className="owner-field-error">{errors.password.message}</p>
        )}
      </div>

      <div className="owner-field">
        <label className="owner-label" htmlFor="confirm">
          Confirm password
        </label>
        <PasswordInput
          id="confirm"
          classPrefix="owner"
          autoComplete="new-password"
          aria-invalid={errors.confirm ? 'true' : undefined}
          {...register('confirm')}
        />
        {errors.confirm && (
          <p className="owner-field-error">{errors.confirm.message}</p>
        )}
      </div>

      <div className="owner-wizard-actions">
        <button
          type="button"
          className="owner-btn"
          onClick={onBack}
          disabled={consume.isPending}
        >
          Back
        </button>
        <button
          type="submit"
          className="owner-btn owner-btn-primary"
          disabled={isSubmitting || consume.isPending}
        >
          {consume.isPending ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </form>
  )
}

// ----- step 1: profile (email + first name) -----

// Two modes:
//  - 'token' (the default /setup/:token flow): hold values locally and
//    hand them up to the parent. The password step (step 2) ships them
//    along with the consume call as a single transaction, since a PATCH
//    /profile can't authenticate before consume opens a session.
//  - 'resume' (/setup/continue): owner is already authenticated, so PATCH
//    /profile directly.
function ProfileStep({
  initialEmail,
  initialDisplayName,
  mode,
  token,
  onLocalDone,
  onResumeDone,
}: {
  initialEmail?: string | undefined
  initialDisplayName?: string | undefined
  mode: 'token' | 'resume'
  token?: string | undefined
  onLocalDone?: (values: OwnerSetupProfileRequest) => void
  onResumeDone?: () => void
}) {
  const profile = useSetupProfile()
  const checkEmail = useSetupCheckEmail()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<OwnerSetupProfileRequest>({
    resolver: zodResolver(OwnerSetupProfileRequestSchema),
    defaultValues: {
      email: initialEmail ?? '',
      display_name: initialDisplayName ?? '',
    },
  })

  function onSubmit(values: OwnerSetupProfileRequest) {
    if (mode === 'token') {
      // Check email availability up front so a taken email is flagged HERE at
      // step 1 (inline on the field) instead of only at the consume step. On any
      // non-availability error, fall through — consume re-validates the email
      // (and the token) anyway, so we never trap the owner on a transient hiccup.
      if (!token) {
        onLocalDone?.(values)
        return
      }
      checkEmail.mutate(
        { token, email: values.email },
        {
          onSuccess: (res) => {
            if (res.available) {
              onLocalDone?.(values)
            } else {
              setError('email', {
                message:
                  'This email is already in use. Use a different one, or sign in instead.',
              })
            }
          },
          onError: () => onLocalDone?.(values),
        },
      )
      return
    }
    profile.mutate(values, {
      onSuccess: () => onResumeDone?.(),
      onError: (err) => {
        if (err instanceof OwnerApiError && err.code === 'CONFLICT') {
          setError('email', { message: err.userMessage })
          return
        }
        setError('root', {
          message:
            err instanceof OwnerApiError
              ? err.userMessage
              : 'Could not save your details. Please try again.',
        })
      },
    })
  }

  const isLoading = mode === 'resume' && profile.isPending
  const isChecking = mode === 'token' && checkEmail.isPending

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="owner-wizard-step-label">Step 1 of {TOTAL_STEPS}</div>
      <h2>Your details</h2>
      <p className="owner-card-sub">
        You'll sign in with this email going forward.
      </p>

      {errors.root && (
        <div className="owner-auth-error" role="alert">
          {errors.root.message}
        </div>
      )}

      <div className="owner-field">
        <label className="owner-label" htmlFor="profile_email">
          Email
        </label>
        <input
          id="profile_email"
          type="email"
          inputMode="email"
          autoComplete="email"
          className="owner-input"
          placeholder="you@example.com"
          aria-invalid={errors.email ? 'true' : undefined}
          {...register('email')}
        />
        {errors.email && (
          <p className="owner-field-error">{errors.email.message}</p>
        )}
      </div>

      <div className="owner-field">
        <label className="owner-label" htmlFor="profile_name">
          First name
        </label>
        <input
          id="profile_name"
          type="text"
          autoComplete="given-name"
          className="owner-input"
          placeholder="Juan"
          aria-invalid={errors.display_name ? 'true' : undefined}
          {...register('display_name')}
        />
        {errors.display_name && (
          <p className="owner-field-error">{errors.display_name.message}</p>
        )}
      </div>

      <div className="owner-wizard-actions">
        <button
          type="submit"
          className="owner-btn owner-btn-primary"
          disabled={isSubmitting || isLoading || isChecking}
        >
          {isLoading ? 'Saving…' : isChecking ? 'Checking…' : 'Continue'}
        </button>
      </div>
    </form>
  )
}

// ----- first branch -----

function BranchStep({
  onDone,
  onBack,
}: {
  onDone: () => void
  onBack: () => void
}) {
  const createBranch = useCreateBranch()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<OwnerCreateBranchRequest>({
    resolver: zodResolver(OwnerCreateBranchRequestSchema),
  })

  function onSubmit(values: OwnerCreateBranchRequest) {
    createBranch.mutate(values, {
      onSuccess: () => onDone(),
      onError: (err) => {
        setError('root', {
          message:
            err instanceof OwnerApiError
              ? err.userMessage
              : 'Could not create the branch. Please try again.',
        })
      },
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="owner-wizard-step-label">Step 5 of {TOTAL_STEPS}</div>
      <h2>Add your first branch</h2>
      <p className="owner-card-sub">
        Where do customers earn stamps? You can add more branches later.
      </p>

      {errors.root && (
        <div className="owner-auth-error" role="alert">
          {errors.root.message}
        </div>
      )}

      <div className="owner-field">
        <label className="owner-label" htmlFor="branch_name">
          Branch name
        </label>
        <input
          id="branch_name"
          type="text"
          className="owner-input"
          placeholder="Gotchaa Main"
          aria-invalid={errors.name ? 'true' : undefined}
          {...register('name')}
        />
        {errors.name && (
          <p className="owner-field-error">{errors.name.message}</p>
        )}
      </div>

      <div className="owner-field">
        <label className="owner-label" htmlFor="branch_address">
          Address <span style={{ textTransform: 'none' }}>(optional)</span>
        </label>
        <input
          id="branch_address"
          type="text"
          className="owner-input"
          placeholder="R. San Juan St. Bgy San Miguel"
          {...register('address')}
        />
      </div>

      <div className="owner-wizard-actions">
        <button
          type="button"
          className="owner-btn"
          onClick={onBack}
          disabled={createBranch.isPending}
        >
          Back
        </button>
        <button
          type="submit"
          className="owner-btn owner-btn-primary"
          disabled={isSubmitting || createBranch.isPending}
        >
          {createBranch.isPending ? 'Creating…' : 'Finish setup'}
        </button>
      </div>
    </form>
  )
}

// ----- expired-link page -----

function ExpiredLink({ reason }: { reason: string }) {
  const message =
    reason === 'consumed'
      ? 'This setup link has already been used.'
      : reason === 'expired'
        ? 'This setup link has expired.'
        : 'This setup link is not valid.'
  return (
    <div className="owner-root">
      <div className="owner-wizard">
        <div className="owner-wizard-card">
          <h2>Link unavailable</h2>
          <p className="owner-card-sub" style={{ marginTop: 8 }}>
            {message}
          </p>
          <p className="owner-hint">
            Please contact Naya Pass support and we'll send you a fresh link.
          </p>
        </div>
      </div>
    </div>
  )
}

// ----- the wizard -----

export function SetupWizardPage({ resume = false }: { resume?: boolean }) {
  const { token } = useParams<{ token: string }>()
  const session = useOwnerSession()
  // All hooks must run unconditionally and in a stable order — the validation
  // query is *registered* every render but disabled (no token) in resume mode.
  const validate = useSetupValidate(resume ? undefined : token)

  // Slice 6 — both modes start at 'profile'. Token mode collects the
  // owner's email + name first (held locally), then password (which
  // submits both to /consume in one transaction). Resume mode PATCHes
  // profile directly via the authenticated session.
  const [step, setStep] = useState<WizardStep>('profile')
  const [pendingProfile, setPendingProfile] = useState<{
    email: string
    display_name: string
  } | null>(null)

  // ----- resume mode -----
  if (resume) {
    if (session.isLoading) {
      return (
        <div className="owner-root">
          <p className="owner-loading">Loading…</p>
        </div>
      )
    }
    // Not authenticated → can't resume; bounce to login.
    if (!session.data) return <Navigate to="/login" replace />
  }

  if (!resume) {
    if (!token) return <Navigate to="/login" replace />
    if (validate.isLoading) {
      return (
        <div className="owner-root">
          <p className="owner-loading">Checking your setup link…</p>
        </div>
      )
    }
    if (validate.isError) {
      return (
        <div className="owner-root">
          <div className="owner-error-state">
            <p>We couldn't check your setup link. Please try again.</p>
          </div>
        </div>
      )
    }
    if (validate.data && validate.data.valid === false) {
      return <ExpiredLink reason={validate.data.reason} />
    }
  }

  if (step === 'complete') {
    return <Navigate to="/dashboard" replace />
  }

  const businessName =
    (!resume && validate.data?.valid ? validate.data.tenant.business_name : null) ??
    session.data?.tenant.name ??
    ''
  const logoUrl = session.data?.tenant.logo_url ?? null
  const logoUpdatedAt = session.data?.tenant.logo_updated_at ?? ''
  // Available once the owner session resolves (after setup-consume in token
  // mode, or immediately in resume mode). The logo step waits for it.
  const defaultLogoUrl = session.data?.default_logo_url ?? null

  return (
    <div className="owner-root">
      <div className="owner-wizard">
        <div className="owner-wizard-card">
          <WizardProgress current={step} />

          {step === 'profile' && (
            <ProfileStep
              mode={resume ? 'resume' : 'token'}
              token={token}
              initialEmail={
                resume ? (session.data?.user.email ?? undefined) : undefined
              }
              initialDisplayName={
                resume
                  ? (session.data?.user.display_name ?? undefined)
                  : undefined
              }
              onLocalDone={(values) => {
                setPendingProfile({
                  email: values.email,
                  display_name: values.display_name,
                })
                setStep('password')
              }}
              onResumeDone={() => setStep('business_name')}
            />
          )}

          {step === 'password' && token && (
            <PasswordStep
              token={token}
              profile={pendingProfile}
              onDone={() => setStep('business_name')}
              onBack={() => setStep('profile')}
            />
          )}

          {step === 'business_name' && (
            <div>
              <div className="owner-wizard-step-label">
                Step 3 of {TOTAL_STEPS}
              </div>
              <h2>Your business name</h2>
              <p className="owner-card-sub">
                This is what customers see on their loyalty pass. Your staff
                sign in with a short code based on this name — you'll find it
                later on the Staff page.
              </p>
              <BusinessNameForm
                initialName={
                  businessName === 'Untitled business' ? '' : businessName
                }
                saveLabel="Continue"
                onSaved={() => setStep('logo')}
              />
            </div>
          )}

          {step === 'logo' && (
            <div>
              <div className="owner-wizard-step-label">
                Step 4 of {TOTAL_STEPS}
              </div>
              <h2>Add your logo</h2>
              <p className="owner-card-sub">
                Optional — you can skip this and use the Naya default for now.
              </p>
              {defaultLogoUrl ? (
                <LogoUploader
                  currentLogoUrl={logoUrl}
                  defaultLogoUrl={defaultLogoUrl}
                  logoUpdatedAt={logoUpdatedAt}
                  onUploaded={() => setStep('branch')}
                />
              ) : (
                <p className="owner-loading">Loading…</p>
              )}
              <div className="owner-wizard-actions">
                <button
                  type="button"
                  className="owner-btn"
                  onClick={() => setStep(prevStep('logo'))}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="owner-btn"
                  onClick={() => setStep('branch')}
                >
                  Skip — use the Naya default
                </button>
              </div>
            </div>
          )}

          {step === 'branch' && (
            <BranchStep
              onDone={() => setStep('complete')}
              onBack={() => setStep(prevStep('branch'))}
            />
          )}
        </div>
        <div style={{ marginTop: 16 }}>
          <OwnerThemeToggle />
        </div>
      </div>
    </div>
  )
}
